/**
 * Centralized authentication error handler
 * Handles 401 errors by redirecting to login with return URL
 *
 * Expected API error structure (from OpenAPI spec):
 * {
 *   statusCode: 401;
 *   message?: string;           // e.g., "Token expired", "Invalid token"
 *   details?: string[];         // Additional error details
 *   codeText?: "REFRESH_NEEDED"; // Explicit indicator for token refresh (NEW!)
 * }
 *
 * The error interceptor receives:
 * - error: The parsed error body (object above)
 * - response: The Response object (with response.status)
 *
 * See: src/proofingService-client/types.gen.ts for complete type definitions
 */

import { config } from "@/config";
import type { HeyApiError } from "@/lib/types";

// Flag to prevent multiple concurrent redirects
let redirectInProgress = false;

/**
 * Reset the redirect flag (useful for testing)
 * @internal
 */
export const resetRedirectFlag = () => {
  redirectInProgress = false;
};

export const handleAuthError = (error?: HeyApiError) => {
  // Prevent multiple concurrent redirects
  if (redirectInProgress) {
    return;
  }

  redirectInProgress = true;
  console.warn("Authentication failed (401). Redirecting to login...");
  const needsRefresh = error?.statusCode === 401 && error?.codeText === "REFRESH_NEEDED";
  const currentUrl = window.location.href;
  const loginUrl = `${config.auth.serviceUrl}/authn/${needsRefresh ? "refresh" : "login"}?return_url=${encodeURIComponent(currentUrl)}`;
  window.location.replace(loginUrl);

  // Safety reset: if navigation is blocked (e.g., in tests), allow future redirects
  setTimeout(() => {
    redirectInProgress = false;
  }, 5000);
};

/**
 * Check if an error is a 401 authentication error
 *
 * Supports the two HTTP clients actually used in this application:
 * 1. hey-api client (proofing service):
 *    - Interceptor receives: error body with `statusCode: 401`, response parameter with `status: 401`
 *    - We check: response.status === 401 OR error.statusCode === 401
 * 2. axios (generic fetcher):
 *    - Error structure: { response: { status: 401 } }
 *    - We check: error.response.status === 401
 *
 * This function has been simplified to only check patterns that actually occur in the codebase,
 * removing hypothetical patterns like error.status, error.error.status, or Response-as-error.
 *
 * @param error - The error object to check
 * @param response - Optional Response object (passed by hey-api interceptor)
 * @returns true if the error is a 401 authentication error
 */
export const isAuthError = (error: unknown, response?: Response): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  // Pattern 1: Response parameter from hey-api interceptor
  if (response && response.status === 401) {
    return true;
  }

  // Pattern 2: API error body from hey-api client (parsed JSON)
  if ("statusCode" in error && error.statusCode === 401) {
    return true;
  }

  // Pattern 3: Axios error structure from generic fetcher
  if ("response" in error && error.response && typeof error.response === "object") {
    if ("status" in error.response && error.response.status === 401) {
      return true;
    }
  }

  return false;
};

/**
 * Check if an error indicates an expired token
 * Expired tokens are refreshable and should trigger token refresh
 *
 * @param error - The error object to check
 * @returns true if the error indicates an expired token
 */
export const isTokenExpired = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  // PREFERRED: Check codeText field for explicit refresh indicator
  if ("codeText" in error && error.codeText === "REFRESH_NEEDED") {
    return true;
  }

  // FALLBACK: Check message field for expiration keywords (for backward compatibility)
  if ("message" in error && typeof error.message === "string") {
    const msg = error.message.toLowerCase();
    if (
      msg.includes("expired") ||
      msg.includes("token expired") ||
      msg.includes("session expired")
    ) {
      return true;
    }
  }

  // FALLBACK: Check details array for expiration information
  if ("details" in error && Array.isArray(error.details)) {
    return error.details.some(
      (detail) => typeof detail === "string" && detail.toLowerCase().includes("expired"),
    );
  }

  return false;
};

/**
 * Check if an error indicates an invalid token (non-refreshable)
 * Invalid tokens should redirect to login without attempting refresh
 *
 * @param error - The error object to check
 * @returns true if the error indicates an invalid or unauthorized token
 */
export const isInvalidToken = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  // Check message field for invalid/unauthorized keywords
  if ("message" in error && typeof error.message === "string") {
    const msg = error.message.toLowerCase();
    // Check for specific invalid token phrases (not just "unauthorized" alone)
    if (
      msg.includes("invalid token") ||
      msg.includes("invalid credentials") ||
      msg.includes("authentication failed")
    ) {
      return true;
    }
  }

  // Check details array for invalid token information
  if ("details" in error && Array.isArray(error.details)) {
    return error.details.some((detail) => {
      if (typeof detail !== "string") return false;
      const detailLower = detail.toLowerCase();
      return (
        detailLower.includes("invalid token") ||
        detailLower.includes("invalid credentials") ||
        detailLower.includes("authentication failed")
      );
    });
  }

  return false;
};

/**
 * Determine if an authentication error is refreshable
 * An error is refreshable if it's a 401 error caused by token expiration
 * (not invalid credentials or insufficient permissions)
 *
 * @param error - The error object to check
 * @param response - Optional Response object
 * @returns true if token refresh should be attempted
 */
export const isRefreshableAuthError = (error: unknown, response?: Response): boolean => {
  // Must be a 401 error
  if (!isAuthError(error, response)) {
    return false;
  }

  // If explicitly marked as invalid token, don't attempt refresh
  if (isInvalidToken(error)) {
    return false;
  }

  // If explicitly marked as expired, attempt refresh
  if (isTokenExpired(error)) {
    return true;
  }

  // Default: attempt refresh for 401 errors unless proven non-refreshable
  // This provides a safe default that attempts refresh unless we know it won't work
  return true;
};
